import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  confirmPatientClinicalProfileApi,
  updatePatientClinicalProfileApi,
  type PatientClinicalProfileView,
  type UpdateClinicalProfileInput,
} from '@/dashboard/api';
import { AcBadge, AcButton, SectionShell } from '@/dashboard/ui';
import { formatTimestampIst } from '@/dashboard/owner/Lead360Panel';
import { consultErrorMessage } from '@/dashboard/doctor/doctor-consult-view';
import { useVerifiedConsultAutosave, type ConsultWriterBoundary } from './ConsultIntakeQuestionsBlock';

/**
 * DOCTOR-OS-EXPERIENCE-V1 (C6 ALLERGY DURABILITY) -- THE INTAKE BAND: the
 * doctor-authored DURABLE clinical truths, first section of the active
 * consult, directly above the per-visit history section.
 *
 * The paper case sheet re-captures allergies every visit; AdoraCare does
 * not: anaesthesia allergy (explicit three-state), medication allergies,
 * and medical alerts live ONCE at patient grain (patient_clinical_profiles)
 * and the doctor CONFIRMS or UPDATES them per visit. The band carries the
 * DURABLE truths ONLY -- this VISIT's medical/dental history stays in the
 * existing case-sheet section below (never duplicated).
 *
 * DELIBERATE ACTS, NEVER AUTOSAVE: these are patient-durable audited
 * writes, so the band saves through an explicit small Save button (PUT
 * clinical-profile, tri-state: only CHANGED fields ride the body) and
 * confirms through the explicit Confirm act (POST confirm). A positive
 * anaesthesia allergy ALSO renders the impossible-to-miss snapshot-header
 * alert (the page passes the live profile up through onProfileChange).
 *
 * AVAILABILITY HONESTY: clinicalProfileAvailable=false retains any permitted
 * patient report and the unavailable-record explanation, never an unusable form.
 * Renders ONLY for a linked patient.
 */

export interface IntakeBandProps {
  /** V14g composes the same mounted writer into Questions; no second store. */
  readonly children?: (controls: IntakeQuestionsProfileControls) => ReactNode;
  readonly onRegisterSave?: (save: (() => Promise<boolean>) | null) => void;
  /** The linked golden-record patient id (null = the band renders nothing;
   *  durable truth needs a patient). */
  readonly patientId: string | null;
  /** The visit anchor for both writers (null until the sheet exists --
   *  the acts disable with an honest hint, never a doomed submit). */
  readonly caseSheetId: number | null;
  /** The server-loaded durable profile (the page's live truth). */
  readonly profile: PatientClinicalProfileView;
  /** False = confirmed profile unavailable; permitted reports remain context. */
  readonly available: boolean;
  /** Locked visits (finalized) render the facts read-only. */
  readonly locked?: boolean;
  readonly scopeKey?: string;
  readonly recoveryBusy?: boolean;
  readonly readProfile?: () => Promise<PatientClinicalProfileView>;
  readonly onRegisterBeforeLeave?: (boundary: (() => Promise<boolean>) | null) => void;
  readonly onRegisterRecovery?: (boundary: ConsultWriterBoundary | null) => void;
  readonly onRecoveryChange?: () => void;
  /** ONE-TRUTH INTAKE (DOCTOR-JOURNEY-COMPOSE-V1, law 2a): the
   *  patient-reported allergy answer from the page's ONE lifted intake read,
   *  rendered as read-only PROVENANCE CONTEXT above the durable fields --
   *  patient-reported prefills, the doctor confirms by authoring the durable
   *  record. Display only; never auto-written into any field. */
  readonly patientReported?: {
    readonly value: string;
    readonly provenanceLabel: string;
  } | null;
  /** Fired with the server's post-write profile (header alert stays live). */
  readonly onProfileChange: (profile: PatientClinicalProfileView) => void;
}

export interface IntakeQuestionsProfileControls {
  readonly question: ReactNode;
  readonly details: ReactNode;
  readonly profile: PatientClinicalProfileView;
  readonly available: boolean;
  readonly busy: boolean;
  readonly confirm: () => Promise<boolean>;
  readonly canConfirm: boolean;
  readonly confirmedThisVisit: boolean;
  readonly beforeLeave: () => Promise<boolean>;
  readonly attention: boolean;
}

type ActState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'error'; message: string };

const ANAESTHESIA_CHOICES: ReadonlyArray<{
  readonly value: 'yes' | 'no' | null;
  readonly label: string;
}> = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: null, label: 'Not recorded' },
];

/**
 * PLAIN LANGUAGE, NOT A MIGRATION ID (founder finding, #780 review).
 *
 * This string printed `pending migration 20260804_2` to a DOCTOR mid-consult.
 * An internal migration identifier tells a clinician nothing they can act on,
 * and it invites exactly the wrong inference -- that the message reports
 * whether a migration has been applied. It does not: this band renders on the
 * server's own availability signal, and the applied-state ledger is the only
 * place that records what is applied.
 *
 * The replacement says what is true for the doctor and what to do instead: the
 * allergy answer is not being STORED on the record yet, so it has to live
 * where the doctor can still see it later. No id, no status claim.
 */
/* ONE ALLERGY TRUTH PER VIEWPORT (the C4 TRUTH-AND-GATE rule this copy now
   obeys). The masthead chip already states the durable-record state -- it
   reads "Allergy: not on file", or "Allergy: pending review" precisely so the
   two never contradict. This line restated that same state one card below it,
   so the screen said the same fact twice and spent a sentence doing it. The
   STATE belongs to the chip; what remains here is the only thing the chip
   cannot carry -- what the doctor should DO about it. */
export const INTAKE_BAND_UNAVAILABLE_COPY =
  'Ask about allergies, and note them in the visit notes.';

export default function IntakeBand(props: IntakeBandProps): JSX.Element | null {
  return <IntakeBandEditor key={props.scopeKey ?? props.patientId ?? 'unlinked'} {...props} />;
}

function IntakeBandEditor({
  children, onRegisterSave,
  patientId,
  caseSheetId,
  profile,
  available,
  locked = false,
  scopeKey, recoveryBusy = false, readProfile, onRegisterBeforeLeave, onRegisterRecovery, onRecoveryChange,
  patientReported = null,
  onProfileChange,
}: IntakeBandProps): JSX.Element | null {
  // A completed old-patient request may still write its original server record,
  // but must never replace the current visit's header/profile or error state.
  const active = useRef(true);
  const confirmedRef = useRef<HTMLSpanElement>(null);
  const focusConfirmed = useRef(false);
  const scope = useRef({ patientId, caseSheetId, epoch: 0 });
  if (scope.current.patientId !== patientId || scope.current.caseSheetId !== caseSheetId) {
    scope.current = { patientId, caseSheetId, epoch: scope.current.epoch + 1 };
  }
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  // Local edit state seeded from the server profile; the diff against the
  // PROP is the dirty set (only changed fields ride the PUT -- tri-state).
  const [anaesthesia, setAnaesthesia] = useState<'yes' | 'no' | null>(
    profile.anaesthesiaAllergy,
  );
  const [medicationAllergies, setMedicationAllergies] = useState(
    profile.medicationAllergies ?? '',
  );
  const [medicalAlerts, setMedicalAlerts] = useState(
    profile.medicalAlerts ?? '',
  );
  const [confirmState, setConfirmState] = useState<ActState>({ kind: 'idle' });
  // Reseed the local fields when the server truth changes identity (a
  // consult-switch or a fresh context load) -- keyed on the patient.
  const identity = scopeKey ?? patientId;
  const [seededFor, setSeededFor] = useState(identity);
  if (seededFor !== identity) {
    setSeededFor(identity);
    setAnaesthesia(profile.anaesthesiaAllergy);
    setMedicationAllergies(profile.medicationAllergies ?? '');
    setMedicalAlerts(profile.medicalAlerts ?? '');
    setConfirmState({ kind: 'idle' });
  }

  const trimmedMedication = medicationAllergies.trim();
  const trimmedAlerts = medicalAlerts.trim();
  const changed: UpdateClinicalProfileInput | null = (() => {
    const patch: Record<string, unknown> = {};
    if (anaesthesia !== profile.anaesthesiaAllergy) {
      patch.anaesthesiaAllergy = anaesthesia;
    }
    if ((trimmedMedication === '' ? null : trimmedMedication) !== (profile.medicationAllergies?.trim() || null)) {
      patch.medicationAllergies = trimmedMedication === '' ? null : trimmedMedication;
    }
    if ((trimmedAlerts === '' ? null : trimmedAlerts) !== (profile.medicalAlerts?.trim() || null)) {
      patch.medicalAlerts = trimmedAlerts === '' ? null : trimmedAlerts;
    }
    if (Object.keys(patch).length === 0 || caseSheetId === null) return null;
    return { caseSheetId, ...patch } as UpdateClinicalProfileInput;
  })();

  const currentFields = { anaesthesiaAllergy: anaesthesia, medicationAllergies: trimmedMedication || null, medicalAlerts: trimmedAlerts || null };
  type Fields = typeof currentFields;
  const fieldsOf = (value: PatientClinicalProfileView): Fields => ({ anaesthesiaAllergy: value.anaesthesiaAllergy, medicationAllergies: value.medicationAllergies?.trim() || null, medicalAlerts: value.medicalAlerts?.trim() || null });
  const sameFields = (a: Fields, b: Fields) => a.anaesthesiaAllergy === b.anaesthesiaAllergy && a.medicationAllergies === b.medicationAllergies && a.medicalAlerts === b.medicalAlerts;
  const draftRef = useRef(currentFields); draftRef.current = currentFields;
  const baselineRef = useRef(fieldsOf(profile));
  const propRef = useRef(profile);
  if (propRef.current !== profile || seededFor !== identity) { propRef.current = profile; baselineRef.current = fieldsOf(profile); }
  const verifiedProfile = useRef<PatientClinicalProfileView | null>(null);
  const explicitSave = useRef<Promise<boolean> | null>(null);
  const confirmPending = useRef<Promise<boolean> | null>(null);
  const [leaveMessage, setLeaveMessage] = useState<string | null>(null);
  const editorRef = useRef<HTMLDetailsElement>(null);
  const dirty = () => !sameFields(draftRef.current, baselineRef.current);
  const verified = useVerifiedConsultAutosave({
    scope: String(identity), enabled: available && patientId !== null && caseSheetId !== null && !locked,
    automatic: false, capture: () => draftRef.current, baseline: () => baselineRef.current,
    same: sameFields, isDirty: dirty, hasContent: () => true,
    formatNow: () => 'just now',
    save: async () => {
      if (patientId === null || caseSheetId === null) throw new Error('Visit record unavailable.');
      const epoch = scope.current.epoch;
      const current = draftRef.current;
      const patch: UpdateClinicalProfileInput = { caseSheetId,
        ...(current.anaesthesiaAllergy !== baselineRef.current.anaesthesiaAllergy ? { anaesthesiaAllergy: current.anaesthesiaAllergy } : {}),
        ...(current.medicationAllergies !== baselineRef.current.medicationAllergies ? { medicationAllergies: current.medicationAllergies } : {}),
        ...(current.medicalAlerts !== baselineRef.current.medicalAlerts ? { medicalAlerts: current.medicalAlerts } : {}),
      };
      const result = await updatePatientClinicalProfileApi(patientId, patch);
      if (!active.current || scope.current.epoch !== epoch) return;
      baselineRef.current = fieldsOf(result.profile);
      setLeaveMessage(null); onProfileChange(result.profile);
    },
    readVerified: async () => {
      if (!readProfile) throw new Error('Profile read unavailable.');
      const epoch = scope.current.epoch;
      const value = await readProfile();
      if (!active.current || scope.current.epoch !== epoch) throw new Error('Visit changed.');
      verifiedProfile.current = value;
      return { value: fieldsOf(value), restore: () => {
        draftRef.current = fieldsOf(value);
        setAnaesthesia(value.anaesthesiaAllergy); setMedicationAllergies(value.medicationAllergies ?? ''); setMedicalAlerts(value.medicalAlerts ?? '');
        setLeaveMessage(null); onProfileChange(value);
      } };
    },
    acknowledge: value => {
      baselineRef.current = value;
      const read = verifiedProfile.current;
      if (read && sameFields(fieldsOf(read), value)) onProfileChange(read);
    },
  });
  const verifiedRef = useRef(verified); verifiedRef.current = verified;
  const saveState = verified.saveState;
  const busy = saveState.kind === 'saving' || confirmState.kind === 'saving' || recoveryBusy;
  const confirmedThisVisit = caseSheetId !== null && profile.lastConfirmedCaseSheetId === caseSheetId;
  useEffect(() => {
    if (focusConfirmed.current && confirmedThisVisit && confirmState.kind === 'saved') {
      focusConfirmed.current = false;
      confirmedRef.current?.focus({ preventScroll: true });
    }
  }, [confirmedThisVisit, confirmState.kind, profile.lastConfirmedAt]);
  const onSave = (): void => {
    if (changed === null || busy || explicitSave.current) return;
    const save = verifiedRef.current.flush(); explicitSave.current = save;
    void save.finally(() => { if (explicitSave.current === save) explicitSave.current = null; });
  };
  const beforeLeave = useRef<() => Promise<boolean>>(async () => true);
  const saveCurrent = useRef<() => Promise<boolean>>(async () => true);
  saveCurrent.current = async () => {
    const epoch = scope.current.epoch;
    const pendingSave = explicitSave.current;
    const pendingConfirmation = confirmPending.current;
    if (pendingSave && !await pendingSave) return false;
    if (pendingConfirmation && !await pendingConfirmation) return false;
    if (!active.current || scope.current.epoch !== epoch) return false;
    if (!dirty() && !verifiedRef.current.boundary.uncertain()) return true;
    if (!available || patientId === null || caseSheetId === null || locked) return false;
    const saved = await verifiedRef.current.flush();
    return saved && active.current && scope.current.epoch === epoch;
  };
  useEffect(() => {
    onRegisterSave?.(() => saveCurrent.current());
    return () => onRegisterSave?.(null);
  }, [identity, onRegisterSave]);
  beforeLeave.current = async () => {
    const epoch = scope.current.epoch;
    await explicitSave.current; await confirmPending.current;
    if (!active.current || scope.current.epoch !== epoch) return false;
    if (dirty() || verifiedRef.current.boundary.uncertain()) {
      setLeaveMessage('Save or discard your changes to the doctor-confirmed record before leaving.');
      if (editorRef.current) { editorRef.current.open = true; editorRef.current.querySelector<HTMLElement>('button,input')?.focus(); }
      return false;
    }
    return true;
  };
  const boundaryRef = useRef<ConsultWriterBoundary>(verified.boundary); boundaryRef.current = verified.boundary;
  useEffect(() => {
    if (!available || patientId === null) return;
    onRegisterBeforeLeave?.(() => beforeLeave.current());
    onRegisterRecovery?.({ dirty, pending: () => boundaryRef.current.pending() || confirmPending.current !== null,
      uncertain: () => boundaryRef.current.uncertain(), message: () => boundaryRef.current.message(),
      prepareRestore: () => boundaryRef.current.prepareRestore(), check: () => boundaryRef.current.check() });
    return () => { onRegisterBeforeLeave?.(null); onRegisterRecovery?.(null); };
  }, [identity, available, patientId, onRegisterBeforeLeave, onRegisterRecovery]);
  useEffect(() => { onRecoveryChange?.(); }, [anaesthesia, medicationAllergies, medicalAlerts, saveState.kind, confirmState.kind, onRecoveryChange]);
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => { if (dirty() || boundaryRef.current.pending() || boundaryRef.current.uncertain() || confirmPending.current) { event.preventDefault(); event.returnValue = ''; } };
    window.addEventListener('beforeunload', warn); return () => window.removeEventListener('beforeunload', warn);
  }, []);

  const onConfirm = (): Promise<boolean> => {
    if (confirmPending.current) return confirmPending.current;
    if (caseSheetId === null || patientId === null || busy || dirty() || locked) return Promise.resolve(false);
    const epoch = scope.current.epoch;
    setConfirmState({ kind: 'saving' });
    const request = confirmPatientClinicalProfileApi(patientId, caseSheetId)
      .then((res) => {
        if (!active.current || scope.current.epoch !== epoch) return false;
        focusConfirmed.current = true;
        setConfirmState({ kind: 'saved' });
        onProfileChange(res.profile);
        return true;
      })
      .catch((err: unknown) => {
        if (!active.current || scope.current.epoch !== epoch) return false;
        setConfirmState({
          kind: 'error',
          message: consultErrorMessage(err).text,
        });
        return false;
      });
    confirmPending.current = request;
    void request.finally(() => { if (confirmPending.current === request) confirmPending.current = null; });
    return request;
  };

  if (patientId === null && !children) return null;

  const reportedLine =
    patientReported !== null ? (
      <p
        className="clinic-workbench__muted consult-intake__reported"
        style={{ overflowWrap: 'anywhere' }}
        data-testid="consult-intake-patient-reported"
      >
        Patient reported allergies: {patientReported.value} (
        {patientReported.provenanceLabel})
      </p>
    ) : null;

  if (!available) {
    if (children) return <>{children({question:<p role="status">The anaesthesia allergy record could not be checked.</p>,details:reportedLine,profile,available:false,busy,confirm:onConfirm,canConfirm:false,confirmedThisVisit,beforeLeave:()=>beforeLeave.current(),attention:false})}</>;
    return (
      <SectionShell eyebrow="Intake" title="Allergies and alerts" testId="consult-intake-band">
        {reportedLine}
        <p className="clinic-workbench__muted" data-testid="consult-intake-unavailable">
          {patientReported !== null
            ? 'The confirmed allergy record could not be checked.'
            : INTAKE_BAND_UNAVAILABLE_COPY}
        </p>
      </SectionShell>
    );
  }


  // ROUND 3 (founder correction 3): a CONFIRMED, UNFLAGGED durable record
  // collapses to the compact band. This is presentation, not proof that
  // unknown medication is negative (CW02); the LARGE card renders when
  // unconfirmed or safety-flagged. Every act stays reachable in both
  // shapes: the folded editor + the confirm act render inside the band.
  const flagged =
    profile.anaesthesiaAllergy === 'yes' || !!profile.medicationAllergies?.trim() || !!profile.medicalAlerts?.trim();
  const compact = !locked && profile.lastConfirmedAt !== null && profile.anaesthesiaAllergy === 'no' && !flagged;
  const editorBlock = (
    <details ref={editorRef} className="consult-intake__editor">
      <summary
        className="consult-intake__editor-summary"
        data-testid="consult-intake-update"
      >
        Update allergies and alerts
      </summary>
      <div className="consult-intake__editor-body">
        <div className="clinic-workbench__field consult-intake__field">
          <span>Anaesthesia allergy</span>
          <div
            className="consult-intake__tristate"
            role="group"
            aria-label="Anaesthesia allergy"
            data-testid="consult-intake-anaesthesia"
          >
            {ANAESTHESIA_CHOICES.map((choice) => (
              <button
                key={choice.label}
                type="button"
                className={
                  anaesthesia === choice.value
                    ? 'consult-intake__choice consult-intake__choice--on'
                    : 'consult-intake__choice'
                }
                aria-pressed={anaesthesia === choice.value}
                disabled={busy || locked}
                onClick={() => { setAnaesthesia(choice.value); verified.onEdit(); setLeaveMessage(null); }}
                data-testid={`consult-intake-anaesthesia-${choice.value ?? 'unset'}`}
              >
                {choice.label}
              </button>
            ))}
          </div>
        </div>
        <label className="clinic-workbench__field consult-intake__field">
          <span>Medication allergies</span>
          <input
            type="text"
            className="clinic-workbench__input"
            maxLength={1000}
            value={medicationAllergies}
            disabled={busy || locked}
            onChange={(e) => { setMedicationAllergies(e.target.value); verified.onEdit(); setLeaveMessage(null); }}
            data-testid="consult-intake-medication"
          />
        </label>
        <label className="clinic-workbench__field consult-intake__field">
          <span>Medical alerts</span>
          <input
            type="text"
            className="clinic-workbench__input"
            maxLength={1000}
            value={medicalAlerts}
            disabled={busy || locked}
            onChange={(e) => { setMedicalAlerts(e.target.value); verified.onEdit(); setLeaveMessage(null); }}
            data-testid="consult-intake-alerts"
          />
        </label>
        {!locked ? (
          <div className="consult-intake__actions">
            <AcButton
              size="sm"
              variant="secondary"
              disabled={busy || changed === null}
              onClick={onSave}
              data-testid="consult-intake-save"
            >
              {saveState.kind === 'saving' ? 'Saving...' : 'Save'}
            </AcButton>
            {saveState.kind === 'clean' ? (
              <span className="consult-intake__state" role="status">
                Saved
              </span>
            ) : null}
            {saveState.kind === 'error' ? (
              <span className="consult-intake__error" role="alert">
                {saveState.message}
              </span>
            ) : null}
            {caseSheetId === null ? (
              <span className="clinic-workbench__muted">
                Save opens once the visit record exists.
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    </details>
  );

  const confirmBlock = (
    <div className="consult-intake__confirm" data-testid="consult-intake-confirm">
      {confirmedThisVisit ? (
        <span ref={confirmedRef} tabIndex={-1} role="status">
          <AcBadge tone="positive" testId="consult-intake-confirmed">Confirmed this visit</AcBadge>
        </span>
      ) : !locked ? (
        <AcButton
          size="sm"
          variant="secondary"
          disabled={busy || caseSheetId === null || changed !== null}
          onClick={onConfirm}
          data-testid="consult-intake-confirm-button"
        >
          {confirmState.kind === 'saving' ? 'Confirming...' : 'Confirm current'}
        </AcButton>
      ) : null}
      <span className="clinic-workbench__muted" data-testid="consult-intake-last-confirmed">
        {profile.lastConfirmedAt !== null
          ? `Last confirmed ${formatTimestampIst(profile.lastConfirmedAt)}`
          : 'Last confirmed: never'}
      </span>
      {changed !== null && !locked ? <span className="clinic-workbench__muted">Save changes before confirming.</span> : null}
      {leaveMessage ? <span role="alert" className="consult-intake__error">{leaveMessage}</span> : null}
      {verified.recoveryMessage && !busy ? <AcButton variant="secondary" onClick={() => void verified.boundary.check()}>Check saved profile</AcButton> : null}
      {caseSheetId === null && !locked ? <span className="clinic-workbench__muted">Confirmation opens once the visit record exists.</span> : null}
      {confirmState.kind === 'error' ? (
        <span className="consult-intake__error" role="alert">{confirmState.message}</span>
      ) : null}
    </div>
  );

  const summaryBlock = (<div className="consult-intake__summary" data-testid="consult-intake-summary" aria-label="Doctor-confirmed record">
          <span className="consult-intake__summary-item">
            <span className="consult-intake__summary-label">Anaesthesia allergy</span>
            {profile.anaesthesiaAllergy === null ? '\u2014' : ANAESTHESIA_CHOICES.find((c) => c.value === profile.anaesthesiaAllergy)?.label}
          </span>
          <span className="consult-intake__summary-item">
            <span className="consult-intake__summary-label">Medication allergies</span>
            {profile.medicationAllergies?.trim() || '\u2014'}
          </span>
          <span className="consult-intake__summary-item">
            <span className="consult-intake__summary-label">Medical alerts</span>
            {profile.medicalAlerts?.trim() || '\u2014'}
          </span>
        </div>);

  if (children) return <>{children({
    question:<div className="consult-question-profile-row" data-testid="consult-anaesthesia-question">
      <span>Allergic to local anaesthesia?</span>
      <div className="consult-question-answers consult-question-profile-choices" role="group" aria-label="Allergic to local anaesthesia?">
        {ANAESTHESIA_CHOICES.map(choice=><AcButton key={choice.value ?? 'unset'} variant="secondary"
          aria-pressed={anaesthesia===choice.value} disabled={busy || locked || patientId===null || caseSheetId===null}
          data-testid={`consult-intake-anaesthesia-${choice.value ?? 'unset'}`}
          onClick={()=>{setAnaesthesia(choice.value);verified.onEdit();setLeaveMessage(null);}}>{choice.value===null?'Not asked':choice.label}</AcButton>)}
      </div>
      {saveState.kind==='error'?<p role="alert">{verified.recoveryMessage ?? saveState.message}</p>:null}
      {caseSheetId===null?<p role="status">Save opens once the visit record exists.</p>:null}
    </div>,
    details:<div data-testid="consult-intake-band" className="consult-question-profile-details">
      <p>Doctor-confirmed record</p>{reportedLine}{summaryBlock}{editorBlock}{confirmBlock}
    </div>,profile,available,busy,confirm:onConfirm,canConfirm:!busy && !locked && changed===null && patientId!==null && caseSheetId!==null && !confirmedThisVisit,confirmedThisVisit,beforeLeave:()=>beforeLeave.current(),attention:leaveMessage!==null || saveState.kind==='error',
  })}</>;

  if (compact) {
    // The COMPACT CONFIRMED BAND: one quiet row -- the summary word, the
    // provenance line when present (law 2a), the confirm state, and the
    // folded editor. Unknown fields stay unknown in both band and masthead.
    return (
      <div
        className="consult-allergy-band"
        data-testid="consult-intake-band"
        data-compact="true"
      >
        <span className="consult-allergy-band__label">Allergies and alerts</span>
        {reportedLine}
        <span className="consult-intake__summary-label" data-testid="consult-intake-record-label">Doctor-confirmed record</span>
        <span className="consult-allergy-band__value">
          Anaesthesia allergy: No. Other allergies and alerts: &mdash;
        </span>
        {confirmBlock}
        {editorBlock}
      </div>
    );
  }

  return (
    <SectionShell
      eyebrow="Intake"
      title="Allergies and alerts"
      testId="consult-intake-band"
    >
      <div className="consult-intake">
        {reportedLine}
        <span className="consult-intake__summary-label" data-testid="consult-intake-record-label">Doctor-confirmed record</span>
        {summaryBlock}
        {editorBlock}
        {confirmBlock}
      </div>
    </SectionShell>
  );
}
